import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Footprints, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useHelp } from "@/context/HelpContext";

const SPOT = "guide-spotlight";

function clearSpot() {
  document.querySelectorAll(`.${SPOT}`).forEach((el) => el.classList.remove(SPOT));
}

/** Floating step-by-step card. Navigates to each step's screen and rings the element it talks about. */
export default function GuideOverlay() {
  const { guide, setGuide, endGuide } = useHelp();
  const location = useLocation();
  const navigate = useNavigate();
  const step = guide ? guide.article.steps[guide.step] : null;
  const route = step ? step.route || (guide.step === 0 ? guide.article.route : null) : null;
  const here = location.pathname + location.search;

  useEffect(() => {
    if (!guide || !route || route === here) return;
    navigate(route);
  }, [guide, route, here, navigate]);

  useEffect(() => {
    if (!step) return undefined;
    clearSpot();
    if (!step.target) return undefined;
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      tries += 1;
      if (el) {
        el.classList.add(SPOT);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        clearInterval(timer);
      } else if (tries > 20) clearInterval(timer);
    }, 250);
    return () => { clearInterval(timer); clearSpot(); };
  }, [step, here]);

  if (!guide) return null;
  const total = guide.article.steps.length;
  const last = guide.step === total - 1;
  return (
    <div className="fixed inset-x-3 sm:inset-x-auto sm:right-6 sm:w-[380px] bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] md:bottom-6 z-[60] animate-page"
      data-testid="guide-overlay" role="dialog" aria-label="Step-by-step guide">
      <div className="rounded-2xl bg-slate-900 text-white shadow-2xl border border-amber-400/40 overflow-hidden">
        <div className="h-1 bg-white/10">
          <div className="h-full bg-amber-400 transition-all duration-300" style={{ width: `${((guide.step + 1) / total) * 100}%` }} />
        </div>
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-400 text-slate-900 flex items-center justify-center shrink-0">
              <Footprints className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold" data-testid="guide-progress">
                Step {guide.step + 1} of {total}
              </div>
              <div className="text-xs text-slate-400 truncate">{guide.article.q}</div>
            </div>
            <button type="button" onClick={endGuide} data-testid="guide-close-btn" aria-label="Close guide"
              className="w-8 h-8 -mr-1 -mt-1 rounded-full flex items-center justify-center text-slate-300 hover:bg-white/10">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-slate-100" data-testid="guide-step-text">{step.text}</p>
          <div className="mt-4 flex items-center gap-2">
            <Button size="sm" variant="ghost" disabled={guide.step === 0} data-testid="guide-back-btn"
              onClick={() => setGuide({ ...guide, step: guide.step - 1 })}
              className="text-slate-300 hover:text-white hover:bg-white/10 disabled:opacity-30">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex-1" />
            {last ? (
              <Button size="sm" onClick={endGuide} data-testid="guide-done-btn"
                className="bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 text-slate-900 font-semibold border-0">
                Done
              </Button>
            ) : (
              <Button size="sm" onClick={() => setGuide({ ...guide, step: guide.step + 1 })} data-testid="guide-next-btn"
                className="bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 text-slate-900 font-semibold border-0">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
